import { use, useLayoutEffect, useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { LiteraryStructureBadge } from "@/components/ui/literary-structure-badge"
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { OutlineDataContext } from '@/data/outline/outline-data-context';
import { getPassageByIdMap } from '@/data/passage/passage-data-service';
import { getOutlinesByBook } from '@/data/outline/outline-data-service';
import { useViewTransitionNavigate } from '@/lib/hooks/use-view-transition';
import { LoadingSkeletonOutline } from '@/data/passage/passage-utils';
import { BIBLE_BOOKS } from '@/lib/utils/bible-utils';
import { CornerDownRight, FileText, FolderOpen, Folder } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

export default function OutlineView() {
    const { data: passageData } = use(PassageDataContext);
    const { data: outlineData } = use(OutlineDataContext);
    const [, navigate] = useViewTransitionNavigate();

    // Get book from URL query parameter, default to Genesis
    const urlParams = new URLSearchParams(window.location.search);
    const bookFromUrl = urlParams.get('book') || BIBLE_BOOKS[0].name;
    const [selectedBook, setSelectedBook] = useState<string>(bookFromUrl);

    // Update URL when book changes
    const handleBookChange = (newBook: string) => {
        setSelectedBook(newBook);
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('book', newBook);
        window.history.pushState({}, '', newUrl.toString());

        // Scroll to top when changing books
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Update selected book when URL changes (e.g., browser back/forward)
    useEffect(() => {
        const handlePopState = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const bookFromUrl = urlParams.get('book') || BIBLE_BOOKS[0].name;
            setSelectedBook(bookFromUrl);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    if (!passageData || !outlineData) {
        return <LoadingSkeletonOutline />;
    }

    // Get outline for the selected book
    const outlinesByBook = getOutlinesByBook(outlineData.items);
    const bookOutlines = outlinesByBook.get(selectedBook) || [];
    const bookOutline = bookOutlines[0]; // Assuming one outline per book

    // Create passage lookup map
    const passageMap = getPassageByIdMap(passageData.items);

    return (
        <div className="flex flex-1 flex-col">
            {/* Sticky Header with Book Title and Dropdown */}
            <div className="sticky top-12 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
                <div className="px-4 py-3 lg:px-6">
                    <div className="flex items-center justify-between gap-4">
                        <Select aria-label="Book Selector" value={selectedBook} onValueChange={handleBookChange}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Select a book" />
                            </SelectTrigger>
                            <SelectContent>
                                {BIBLE_BOOKS.map(book => (
                                    <SelectItem key={book.name} value={book.name}>
                                        {book.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>
            <RenderOutline outline={bookOutline} passageMap={passageMap} navigate={navigate} />
        </div>
    );
}

function RenderOutline({ outline, passageMap, navigate }: {
    outline?: Outline;
    passageMap: Map<string, Passage>;
    navigate: (path: string) => void;
}) {

    useLayoutEffect(() => {
        // When a user navigates back to this list view, we want to restore their
        // scroll position. useLayoutEffect ensures this happens synchronously
        // before the browser captures the new state for the view transition.
        const scrollPos = sessionStorage.getItem('passagesScrollPos');
        if (scrollPos) {
            window.scrollTo(0, parseInt(scrollPos, 10));
            sessionStorage.removeItem('passagesScrollPos');
        }
    }, []);

    if (!outline) {
        return (
            <div className="flex flex-1 flex-col gap-2 px-4 py-4 lg:px-6">
                <p className="text-muted-foreground">No outline available for this book.</p>
            </div>
        );
    }

    return (
        <div className="@container/main flex flex-1 flex-col gap-2">
            <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
                {/* Outline Structure */}
                <div className="space-y-1">
                    {outline.structure.map((node, index) => (
                        <OutlineNode
                            key={node.id}
                            node={node}
                            passageMap={passageMap}
                            navigate={navigate}
                            depth={0}
                            isLast={index === outline.structure.length - 1}
                            parentConnectors={[]}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function OutlineNode({
    node,
    passageMap,
    navigate,
    depth,
    isLast = false,
    parentConnectors = []
}: {
    node: OutlineNode;
    passageMap: Map<string, Passage>;
    navigate: (path: string) => void;
    depth: number;
    isLast?: boolean;
    parentConnectors?: boolean[];
}) {
    const isDarkMode = useDarkMode();
    const hasChildren = node.children && node.children.length > 0;
    const hasContent = node.sectionIds && node.sectionIds.length > 0;
    const hasMultiplePassages = hasContent && node.sectionIds.length > 1;
    const isTrueLeaf = !hasChildren && hasContent && !hasMultiplePassages; // Single passage, no children
    const [isExpanded, setIsExpanded] = useState(true); // Start everything expanded

    // Get genre color for leaf nodes
    const getGenreColor = (genre: string, isDarkMode: boolean) => {
        const colors = {
            Narrative: isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800',
            Poetry: isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800',
            Wisdom: isDarkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800',
            Prophecy: isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800',
            Epistle: isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800',
            Law: isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-800',
            // Legacy support for old genre names
            narrative: isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800',
            poetry: isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800',
            wisdom: isDarkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800',
            prophecy: isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800',
            epistle: isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800',
            apocalyptic: isDarkMode ? 'bg-orange-900 text-orange-200' : 'bg-orange-100 text-orange-800',
            law: isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-800'
        };
        return colors[genre as keyof typeof colors] || colors.Narrative;
    };

    // Get first passage for genre info
    const firstPassage = hasContent ? passageMap.get(node.sectionIds[0]) : null;

    const handleCardClick = () => {
        if (isTrueLeaf && firstPassage) {
            // True leaf node with single passage: Navigate to passage content
            sessionStorage.setItem('passagesScrollPos', window.scrollY.toString());
            navigate(`/app/passage/${firstPassage.id}`);
        } else {
            // Parent node or multi-passage node: Toggle expand/collapse
            setIsExpanded(!isExpanded);
        }
    };

    return (
        <div className="relative">
            {/* Tree connectors */}
            <div className="flex">
                {/* Render parent connectors */}
                {parentConnectors.map((hasConnector, index) => (
                    <div key={index} className="w-4 sm:w-8 flex justify-center">
                        {hasConnector && (
                            <div className="w-px bg-border h-full"></div>
                        )}
                    </div>
                ))}

                {/* Current level connector */}
                {depth > 0 && (
                    <div className="w-4 sm:w-8 flex flex-col items-center">
                        <div className="w-px bg-border h-4"></div>
                        <div className="flex items-center">
                            <div className="w-2 sm:w-4 h-px bg-border"></div>
                            <CornerDownRight className="w-3 h-3 text-muted-foreground ml-0.5 sm:ml-1" />
                        </div>
                        {!isLast && <div className="w-px bg-border flex-1"></div>}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1">
                    <Card
                        className={`transition-all duration-200 cursor-pointer group backdrop-blur-[2px] py-0 ${isDarkMode ? 'bg-background/50' : 'bg-background/70'} ${isTrueLeaf
                            ? 'hover:bg-accent/20 hover:shadow-md hover:border-primary/40 hover:scale-[1.01] border-dashed hover:border-solid'
                            : 'hover:bg-accent/15 hover:shadow-sm hover:border-amber-300'
                            } ${depth === 0 ? 'border-2 border-primary/20' : ''
                            } ${depth === 1 ? 'border-l-4 border-l-primary/40' : ''} ${depth >= 2 ? 'border-l-2 border-l-muted-foreground/30' : ''
                            }`}
                        onClick={handleCardClick}
                    >
                        <CardContent className={`py-2 px-2`}>
                            <div className="flex items-start gap-2">
                                {/* Node type indicator */}
                                <div className="flex items-center mt-0.5">
                                    {!isTrueLeaf ? (
                                        <div className="p-1 rounded transition-all group-hover:bg-accent/20">
                                            {isExpanded ? (
                                                <FolderOpen className="w-5 h-5 text-amber-600" />
                                            ) : (
                                                <Folder className="w-5 h-5 text-amber-600" />
                                            )}
                                        </div>
                                    ) : (
                                        <FileText className="w-4 h-4 text-blue-600" />
                                    )}
                                </div>

                                <div className="flex-1 min-w-0">
                                    {/* Title with verse reference */}
                                    <div className="mb-0.5">
                                        {/* Desktop layout: title and badges on same line */}
                                        <div className="hidden sm:flex sm:items-center sm:gap-1.5">
                                            <h3 className={`font-medium leading-tight flex-1 ${depth === 0 ? 'text-base text-primary' :
                                                depth === 1 ? 'text-sm' : 'text-xs'
                                                } ${isTrueLeaf ? 'group-hover:text-primary transition-colors' : ''}`}>
                                                <span className={`inline-flex items-center font-mono text-sm px-2 py-0.5 rounded-md mr-2 border ${isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                                                    {node.ref}
                                                </span>
                                                {node.title}
                                            </h3>

                                            {/* Badges on the right for desktop */}
                                            <div className="flex gap-1 flex-shrink-0">
                                                {/* Badges for parent nodes */}
                                                {!isTrueLeaf && (
                                                    <>
                                                        {/* Literary structure badges */}
                                                        {node.chiasm && (
                                                            <LiteraryStructureBadge structure="chiasm" />
                                                        )}
                                                        {node.inclusio && (
                                                            <LiteraryStructureBadge structure="inclusio" />
                                                        )}
                                                        {/* Passage count badge - rightmost for alignment */}
                                                        {hasMultiplePassages && !isExpanded && (
                                                            <Badge variant="outline" className="text-xs h-5 bg-amber-50 text-amber-700 border-amber-200">
                                                                {node.sectionIds?.length || 0} {node.sectionIds?.length === 1 ? 'passage' : 'passages'} collapsed
                                                            </Badge>
                                                        )}
                                                        {/* Children count badge - rightmost for alignment */}
                                                        {hasChildren && !isExpanded && (
                                                            <Badge variant="outline" className="text-xs h-5 bg-blue-50 text-blue-700 border-blue-200">
                                                                {node.children?.length || 0} {node.children?.length === 1 ? 'child' : 'children'} collapsed
                                                            </Badge>
                                                        )}
                                                    </>
                                                )}

                                                {/* Genre badges for leaf nodes */}
                                                {isTrueLeaf && firstPassage && (
                                                    <>
                                                        <Badge
                                                            className={`text-xs h-5 ${getGenreColor(firstPassage.genre, isDarkMode)} group-hover:shadow-sm transition-shadow`}
                                                            variant="outline"
                                                        >
                                                            {firstPassage.genre}
                                                        </Badge>
                                                        {firstPassage.subgenre && (
                                                            <Badge variant="outline" className="text-xs h-5 group-hover:shadow-sm transition-shadow">
                                                                {firstPassage.subgenre}
                                                            </Badge>
                                                        )}
                                                        {firstPassage.form && (
                                                            <Badge variant="outline" className="text-xs h-5 group-hover:shadow-sm transition-shadow">
                                                                {firstPassage.form}
                                                            </Badge>
                                                        )}
                                                        {firstPassage.parallelism && (
                                                            <LiteraryStructureBadge
                                                                structure="parallelism"
                                                                className="group-hover:shadow-sm transition-shadow"
                                                            />
                                                        )}
                                                        {firstPassage.transition && (
                                                            <LiteraryStructureBadge
                                                                structure="transition"
                                                                className="group-hover:shadow-sm transition-shadow"
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Mobile layout: title on top, badges below */}
                                        <div className="sm:hidden">
                                            <h3 className={`font-medium leading-tight ${depth === 0 ? 'text-base text-primary' :
                                                depth === 1 ? 'text-sm' : 'text-xs'
                                                } ${isTrueLeaf ? 'group-hover:text-primary transition-colors' : ''}`}>
                                                <span className={`inline-flex items-center font-mono text-sm px-2 py-0.5 rounded-md mr-2 border ${isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                                                    {node.ref}
                                                </span>
                                                {node.title}
                                            </h3>

                                            {/* Badges below title for mobile */}
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {/* Badges for parent nodes */}
                                                {!isTrueLeaf && (
                                                    <>
                                                        {/* Literary structure badges */}
                                                        {node.chiasm && (
                                                            <LiteraryStructureBadge structure="chiasm" className="h-4" />
                                                        )}
                                                        {node.inclusio && (
                                                            <LiteraryStructureBadge structure="inclusio" className="h-4" />
                                                        )}
                                                        {/* Passage count badge - for multi-passage nodes */}
                                                        {hasMultiplePassages && (
                                                            <Badge variant="outline" className="text-xs h-4 bg-amber-50 text-amber-700 border-amber-200">
                                                                {node.sectionIds?.length || 0} {node.sectionIds?.length === 1 ? 'passage' : 'passages'}
                                                            </Badge>
                                                        )}
                                                        {/* Children count badge - for nodes with children */}
                                                        {hasChildren && !isExpanded && (
                                                            <Badge variant="outline" className="text-xs h-4 bg-blue-50 text-blue-700 border-blue-200">
                                                                {node.children?.length || 0} {node.children?.length === 1 ? 'child' : 'children'} collapsed
                                                            </Badge>
                                                        )}
                                                    </>
                                                )}

                                                {/* Genre badges for leaf nodes */}
                                                {isTrueLeaf && firstPassage && (
                                                    <>
                                                        <Badge
                                                            className={`text-xs h-4 ${getGenreColor(firstPassage.genre, isDarkMode)} group-hover:shadow-sm transition-shadow`}
                                                            variant="outline"
                                                        >
                                                            {firstPassage.genre}
                                                        </Badge>
                                                        {firstPassage.parallelism && (
                                                            <LiteraryStructureBadge
                                                                structure="parallelism"
                                                                className="h-4 group-hover:shadow-sm transition-shadow"
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Show summary for leaf nodes */}
                                    {/* {isTrueLeaf && firstPassage && (
                                        <p className="text-xs text-muted-foreground leading-snug">
                                            {firstPassage.summary}
                                        </p>
                                    )} */}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Render children */}
            {hasChildren && isExpanded && (
                <div className="mt-0.5 transition-all duration-200">
                    {node.children!.map((child, index) => (
                        <OutlineNode
                            key={child.id}
                            node={child}
                            passageMap={passageMap}
                            navigate={navigate}
                            depth={depth + 1}
                            isLast={index === node.children!.length - 1}
                            parentConnectors={[...parentConnectors, !isLast]}
                        />
                    ))}
                </div>
            )}

            {/* Render individual passages for multi-passage nodes */}
            {hasMultiplePassages && isExpanded && (
                <div className="mt-0.5 transition-all duration-200">
                    {node.sectionIds.map((passageId) => {
                        const passage = passageMap.get(passageId);
                        if (!passage) {
                            console.warn(`Passage not found for ID: ${passageId}`);
                            return null;
                        }

                        const handlePassageClick = () => {
                            sessionStorage.setItem('passagesScrollPos', window.scrollY.toString());
                            navigate(`/app/passage/${passage.id}`);
                        };

                        return (
                            <div key={passageId} className="relative">
                                {/* Tree connectors for passages */}
                                <div className="flex">
                                    {/* Render parent connectors */}
                                    {parentConnectors.map((hasConnector, connectorIndex) => (
                                        <div key={connectorIndex} className="w-4 sm:w-8 flex justify-center">
                                            {hasConnector && (
                                                <div className="w-px bg-border h-full"></div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Current level connector for the parent node */}
                                    <div className="w-4 sm:w-8 flex justify-center relative">
                                        <div className="w-px bg-border h-6"></div>
                                        <div className="absolute top-6 w-2 sm:w-4 h-px bg-border"></div>
                                    </div>

                                    {/* Additional indentation for passages (they are children of the node) */}
                                    <div className="w-4 sm:w-8 flex justify-center relative">
                                        <div className="w-px bg-border h-6"></div>
                                        <div className="absolute top-6 w-2 sm:w-4 h-px bg-border"></div>
                                        <CornerDownRight className="absolute top-5 right-0 w-3 h-3 text-muted-foreground" />
                                    </div>

                                    {/* Passage card */}
                                    <div className="flex-1 pb-1">
                                        <Card
                                            className={`cursor-pointer transition-all duration-200 backdrop-blur-[2px] py-0 hover:bg-accent/20 hover:shadow-md hover:border-primary/40 hover:scale-[1.01] border-dashed hover:border-solid ${isDarkMode ? 'bg-background/50' : 'bg-background/70'}`}
                                            onClick={handlePassageClick}
                                        >
                                            <CardContent className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-blue-600" />
                                                    <div className="flex-1">
                                                        <h4 className="text-sm font-medium group-hover:text-primary transition-colors">
                                                            <span className={`inline-flex items-center font-mono text-xs px-2 py-0.5 rounded-md mr-2 border ${isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                                                                {passage.ref}
                                                            </span>
                                                            {passage.title}
                                                        </h4>
                                                    </div>
                                                    <Badge
                                                        className={`text-xs h-5 ${getGenreColor(passage.genre, isDarkMode)} group-hover:shadow-sm transition-shadow`}
                                                        variant="outline"
                                                    >
                                                        {passage.genre}
                                                    </Badge>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
